import { memo, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BottomSheet } from '@turnifly/shared/ui'
import { useLifeBalanceStore, type SegmentId } from '@turnifly/shared/stores'

interface SegmentConfig {
  id: SegmentId
  labelKey: string
  emoji: string
  color: string
}

interface WheelSegmentSheetProps {
  isOpen: boolean
  segmentId: SegmentId
  config: SegmentConfig
}

export const WheelSegmentSheet = memo(function WheelSegmentSheet({
  isOpen,
  segmentId,
  config,
}: WheelSegmentSheetProps) {
  const { t } = useTranslation()
  const { segments: segmentsList, setScore, setEditing } = useLifeBalanceStore()
  const currentScore = segmentsList.find((s) => s.id === segmentId)?.score ?? 5

  const handleSelect = useCallback((score: number) => {
    setScore(segmentId, score)
  }, [segmentId, setScore])

  const handleClose = useCallback(() => {
    setEditing(null)
  }, [setEditing])

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={`${config.emoji} ${t(config.labelKey)}`}
    >
      <div className="px-5 pb-6">
        {/* Score grid */}
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((score) => {
            const isActive = score === currentScore
            return (
              <motion.button
                key={score}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleSelect(score)}
                className={`
                  relative flex items-center justify-center h-12 rounded-xl
                  font-display font-bold text-base tap-highlight-transparent transition-all
                  ${isActive
                    ? 'text-white border-2'
                    : 'bg-white/[0.03] border border-white/[0.06] text-hint'
                  }
                `}
                style={isActive ? {
                  backgroundColor: `${config.color}20`,
                  borderColor: `${config.color}60`,
                  color: config.color,
                  boxShadow: `0 0 16px ${config.color}25`,
                } : undefined}
              >
                {score}
              </motion.button>
            )
          })}
        </div>

        {/* Score descriptions */}
        <div className="flex items-center justify-between mt-3 px-1">
          <span className="text-[10px] text-hint">{t('life.balance.low')}</span>
          <span className="text-[10px] text-hint">{t('life.balance.high')}</span>
        </div>
      </div>
    </BottomSheet>
  )
})
